import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useSyncExternalStore } from 'react';
import { ApiError, get } from './api.ts';

export interface Me {
  userId: string;
  email: string;
  displayName: string;
  isSuperadmin: boolean;
  mustChangePassword: boolean;
  employee: null | {
    id: string;
    employeeNo: string | null;
    givenName: string;
    familyName: string | null;
    officeId: string;
    officeName: string | null;
    timeZone: string | null;
    jobPosition: string | null;
    division: string | null;
  };
  roles: { app: string; key: string }[];
  access: Record<
    'people' | 'peopleAll' | 'sensitive' | 'recruit' | 'approvals' | 'attendance' | 'attendanceAdmin' | 'shiftEdit' | 'payroll' | 'payrollRun' | 'payrollApprove' | 'settings' | 'devices' | 'deviceUsers' | 'cooperative' | 'canteen' | 'developer' | 'goodsPurchase' | 'master' | 'masterEdit' | 'access' | 'employeeEdit' | 'resignationManage' | 'salaryUpdate' | 'componentEdit' | 'orgEdit',
    boolean
  >;
  /** Set while someone with HR access looks at Hearth as a regular employee ("My view"). */
  viewingAsEmployee?: boolean;
}

// ---------------------------------------------------------------- HR view / My view
// Purely a lens on the screen: the server still answers with the person's real permissions.

type View = 'staff' | 'personal';
const VIEW_KEY = 'hearth.view';
const viewListeners = new Set<() => void>();
let currentView: View = (() => {
  try {
    return localStorage.getItem(VIEW_KEY) === 'personal' ? 'personal' : 'staff';
  } catch {
    return 'staff';
  }
})();

export function setView(view: View) {
  currentView = view;
  try {
    localStorage.setItem(VIEW_KEY, view);
  } catch {
    /* private mode */
  }
  viewListeners.forEach((l) => l());
}

const subscribeView = (l: () => void) => {
  viewListeners.add(l);
  return () => viewListeners.delete(l);
};

/** Someone who sees more than their own records, for whom "My view" looks different. */
export const hasStaffAccess = (me: Me) => Object.entries(me.access).some(([k, v]) => v && k !== 'master');

/** Hearth as a regular employee sees it: their own records and nothing else. */
function asEmployee(me: Me): Me {
  const access = Object.fromEntries(Object.keys(me.access).map((k) => [k, k === 'master'])) as Me['access'];
  return { ...me, isSuperadmin: false, roles: [], access, viewingAsEmployee: true };
}

export function useMe() {
  const view = useSyncExternalStore(subscribeView, () => currentView);
  const select = useCallback((me: Me | null) => (me && view === 'personal' && me.employee && hasStaffAccess(me) ? asEmployee(me) : me), [view]);
  return useQuery<Me | null, Error, Me | null>({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await get<Me>('/api/auth/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
    select,
  });
}

export function useSetMe() {
  const qc = useQueryClient();
  return (me: Me | null) => qc.setQueryData(['me'], me);
}

export interface Lookups {
  companies: { id: string; code: string; name: string; isPrimary: boolean }[];
  offices: { id: string; code: string | null; name: string; companyId: string; timeZone: string }[];
  religions: { id: string; name: string }[];
  maritalStatuses: { id: string; name: string }[];
  educationLevels: { id: string; name: string }[];
  banks: { id: string; code: string | null; name: string }[];
  jobPositions: { id: string; name: string }[];
  jobLevels: { id: string; name: string; rank: number }[];
  feastDays: { id: string; name: string }[];
}

export function useLookups() {
  return useQuery<Lookups>({ queryKey: ['lookups'], queryFn: () => get('/api/org/lookups'), staleTime: 5 * 60_000 });
}

export const roleLabel = (me: Me) => {
  const hr = me.roles.find((r) => r.app === 'hr');
  if (me.isSuperadmin && !hr) return 'Platform administrator';
  return hr ? hr.key.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()) : me.employee?.jobPosition ?? 'Employee';
};
